import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  MenuItem,
  Select,
  FormControl,
  Stack,
  Paper,
  Divider,
} from "@mui/material";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import QuillEditor from "../../../components/QuilEditor/QuilEditor";
import { quillHtmlToEmailBody } from "../../../components/QuilEditor/quillHtmlToEmailBody";
import { useToast } from "../../../context/ToastContext";
import { usePortal } from "../../../context/PortalsContext";
import {
  createUpdateEmailTemplate,
  getEmailTemplate,
  getEmailTemplateTypes,
} from "../../../services/api/apiClient";
import {
  EmailTemplateTypeResult,
  EmailTemplateTypeEnum,
  FrequencyUnitEnum,
} from "../../../services/api/types/emailTemplates";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { usePageMetadata } from "../../../context/PageMetadataContext";
import { brand } from "../../../theme/themePrimitives";
import { ChangeEventType } from "../../Assignments/AssignmentUploads/AssignmentUploads";
import { toBase64Utf8 } from "../../../utils/helpers";
import { ReactComponent as ScheduleIcon } from "../../../assets/schedule.svg";
import { ReactComponent as RepeatIcon } from "../../../assets/repeat.svg";

const EmailTemplateEditor: React.FC = () => {
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [sender, setSender] = useState("elearning@igacademy.com");
  const [emailTemplateType, setEmailTemplateType] = useState<
    EmailTemplateTypeEnum | ""
  >("");
  const [isReminder, setIsReminder] = useState(false);
  const [startOffsetDays, setStartOffsetDays] = useState<number | undefined>(
    undefined,
  );
  const [frequencyInterval, setFrequencyInterval] = useState<number>(1);
  const [frequencyUnit, setFrequencyUnit] = useState<FrequencyUnitEnum>(
    FrequencyUnitEnum.DAYS,
  );
  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [templateTypes, setTemplateTypes] = useState<EmailTemplateTypeResult[]>(
    [],
  );
  const { auth } = useAuth();
  const { showToast } = useToast();
  const { portals } = usePortal();
  const { id: templateId, portalUid } = useParams<{
    id?: string;
    portalUid: string;
  }>();
  const navigate = useNavigate();
  const isEditMode = Boolean(templateId);

  const validateFields = (): boolean => {
    const newErrors: Record<string, string> = {};
    // The static fields
    if (!name.trim()) newErrors.name = "Name is required.";
    if (!subject.trim()) newErrors.subject = "Subject is required.";
    if (!emailTemplateType.trim())
      newErrors.emailTemplateType = "Template type is required.";
    if (isReminder && !startOffsetDays)
      newErrors.startoffsetdays = "This field is required";

    console.log(newErrors);
    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: ChangeEventType, elementName: string) => {
    const { name, value } = e.target;
    if (value) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    } else {
      setErrors((prev) => ({ ...prev, [name]: `${elementName} is required` }));
    }
  };

  const handleSave = () => {
    if (!portals.selectedPortal?.portalUid) {
      showToast("No portal selected", "error");
      return;
    }
    if (!auth.user?.UserPortalUid) {
      showToast("User not authenticated", "error");
      return;
    }

    const isValid = validateFields();
    if (!isValid) return;

    const portalId = portals.selectedPortal.portalUid;
    const payload: {
      emailTemplateId?: string;
      emailTemplateTypeUid?: string;
      subject?: string;
      message?: string;
      name?: string;
      sender?: string;
      isReminder?: boolean;
      startOffsetDays?: number;
      frequencyInterval?: number;
      frequencyUnit?: FrequencyUnitEnum;
      adminUser: string;
    } = {
      adminUser: auth.user.UserPortalUid,
    };

    // For update, emailTemplateId is required
    if (isEditMode && templateId) {
      payload.emailTemplateId = templateId;
    }

    // Backend requires emailTemplateTypeUid (not the type name)
    const selectedType = templateTypes.find(
      (t) => t.name === emailTemplateType,
    );
    if (selectedType) {
      payload.emailTemplateTypeUid = selectedType.emailTemplateTypeUid;
    }

    // Add optional fields only if they have values
    if (name) payload.name = name;
    if (subject) payload.subject = subject;
    if (sender) payload.sender = sender;
    if (message) payload.message = toBase64Utf8(quillHtmlToEmailBody(message));

    // Add reminder fields if isReminder is true
    if (isReminder) {
      payload.isReminder = true;
      if (startOffsetDays !== undefined) {
        payload.startOffsetDays = startOffsetDays;
      }
      payload.frequencyInterval = frequencyInterval;
      payload.frequencyUnit = frequencyUnit;
    }

    setLoading(true);
    createUpdateEmailTemplate(portalId, payload)
      .then(() => {
        showToast("Template saved successfully", "success");
        setTimeout(() => {
          navigate(`/portal-configuration/${portalId}/email-templates`);
        }, 1500);
      })
      .catch(() => {
        showToast("Failed to save email template", "error");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const loadTemplateTypes = async () => {
      if (!portals.selectedPortal?.portalUid) return;
      try {
        const portalId = portals.selectedPortal.portalUid;
        const params: {
          portalId: string;
          onlyManual?: string;
        } = { portalId };

        // When creating a new template, restrict to manual types only.
        // When editing, load all types so the existing type from the API is always present and can be selected.
        if (!isEditMode) {
          params.onlyManual = "true";
        }

        const types = await getEmailTemplateTypes(portalId, params);
        setTemplateTypes(types || []);
      } catch (err) {
        showToast("Failed to load template types", "error");
      }
    };
    loadTemplateTypes();
  }, [portals.selectedPortal, isEditMode, showToast]);

  useEffect(() => {
    if (
      emailTemplateType === EmailTemplateTypeEnum.USER_REMINDER ||
      emailTemplateType === EmailTemplateTypeEnum.MANAGER_REMINDER
    ) {
      setIsReminder(true);
    }
  }, [emailTemplateType]);

  useEffect(() => {
    const load = async () => {
      if (!templateId || !portals.selectedPortal?.portalUid) return;

      setLoading(true);
      try {
        const data = await getEmailTemplate(
          portals.selectedPortal.portalUid,
          templateId,
        );
        if (data) {
          setName(data.name || "");
          setSubject(data.subject || "");
          setSender(data.sender || "elearning@igacademy.com");
          setEmailTemplateType(data.type || "");
          setIsReminder(data.isReminder || false);
          setStartOffsetDays(data.startOffsetDays);
          setFrequencyInterval(data.frequencyInterval || 1);
          setFrequencyUnit(data.frequencyUnit || FrequencyUnitEnum.DAYS);
          setMessage(data.message || "");
        }
      } catch (err) {
        showToast("Failed to load email template", "error");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [templateId, portals.selectedPortal, showToast]);

  usePageMetadata(
    {
      title: isEditMode ? "Edit email template" : "Add email template",
      breadcrumbs: [
        {
          label: "Email management",
          path: `/portal-configuration/${portalUid}/email-send`,
        },
        {
          label: "Email Templates",
          path: `/portal-configuration/${portalUid}/email-templates`,
        },
        {
          label: isEditMode ? "Edit email template" : "Add email template",
        },
      ],
    },
    [portalUid, isEditMode],
  );

  return (
    <Paper sx={{ p: 3 }}>
      <Box>
        <Typography variant="h5" fontWeight={600} mb={2}>
          Template details
        </Typography>

        <Divider sx={{ mb: 2 }} />
        <Stack spacing={2.5}>
          {/* Template type */}
          <Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 0.75, fontSize: "0.875rem" }}
            >
              Template type
              <Box component="span" sx={{ color: `var(--error-700)` }}>
                *
              </Box>
            </Typography>
            <FormControl fullWidth>
              <Select
                value={emailTemplateType}
                onChange={(e) => {
                  setEmailTemplateType(e.target.value);
                  handleChange(e, "Template type");
                }}
                displayEmpty
                name="emailTemplateType"
                size="small"
                disabled={isEditMode}
                renderValue={(selected) => {
                  if (!selected) {
                    // When editing, show the template type value from API as placeholder
                    return isEditMode && emailTemplateType
                      ? emailTemplateType
                      : "Choose a type";
                  }
                  return selected;
                }}
              >
                <MenuItem value="" disabled>
                  {isEditMode && emailTemplateType
                    ? emailTemplateType
                    : "Choose a type"}
                </MenuItem>
                {emailTemplateType &&
                  !templateTypes.some((t) => t.name === emailTemplateType) && (
                    <MenuItem key={emailTemplateType} value={emailTemplateType}>
                      {emailTemplateType}
                    </MenuItem>
                  )}
                {templateTypes.map((type) => (
                  <MenuItem key={type.name} value={type.name}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
              {errors?.emailTemplateType && (
                <Typography variant="caption" color="error">
                  {errors.emailTemplateType}
                </Typography>
              )}
            </FormControl>
          </Box>

          {/* Reminder settings */}
          {isReminder && (
            <Stack
              direction="row"
              spacing={2}
              sx={{ backgroundColor: brand[50], padding: 2, borderRadius: 1 }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={{
                    display: "flex",
                    width: "28px",
                    height: "28px",
                    justifyContent: "center",
                    alignItems: "center",
                    borderRadius: 0.5,
                    border: `0.5px solid ${brand[200]}`,
                    backgroundColor: brand[100],
                  }}
                >
                  <ScheduleIcon style={{ color: brand[700] }} />
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.75, fontSize: "0.875rem" }}
                >
                  Send first reminder
                  <Box component="span" sx={{ color: `var(--error-700)` }}>
                    *
                  </Box>
                </Typography>
                <TextField
                  type="number"
                  size="small"
                  name="startoffsetdays"
                  value={startOffsetDays ?? ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === "") {
                      setStartOffsetDays(undefined);
                    } else {
                      const num = parseInt(val, 10);
                      if (!Number.isNaN(num)) {
                        setStartOffsetDays(Math.min(999, Math.max(0, num)));
                      }
                    }
                    handleChange(e, "First reminder");
                  }}
                  error={Boolean(errors.startoffsetdays)}
                  helperText={errors.startoffsetdays || ""}
                  placeholder="Enter days"
                  inputProps={{ min: 0, max: 999, inputMode: "numeric" }}
                  sx={{
                    minWidth: 60,
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "background.paper",
                    },
                  }}
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.75, fontSize: "0.875rem" }}
                >
                  days before due date
                </Typography>
              </Stack>
              <Divider orientation="vertical" flexItem />
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={{
                    display: "flex",
                    width: "28px",
                    height: "28px",
                    justifyContent: "center",
                    alignItems: "center",
                    borderRadius: 0.5,
                    border: `0.5px solid ${brand[200]}`,
                    backgroundColor: brand[100],
                  }}
                >
                  <RepeatIcon style={{ color: brand[700] }} />
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.75, fontSize: "0.875rem" }}
                >
                  Repeat every
                  <Box component="span" sx={{ color: `var(--error-700)` }}>
                    *
                  </Box>
                </Typography>
                <FormControl size="small" sx={{ minWidth: 80 }}>
                  <Select
                    value={frequencyInterval}
                    onChange={(e) =>
                      setFrequencyInterval(e.target.value as number)
                    }
                    IconComponent={KeyboardArrowDown}
                  >
                    {Array.from({ length: 99 }, (_, i) => i + 1).map((num) => (
                      <MenuItem key={num} value={num}>
                        {num}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <Select
                    value={frequencyUnit}
                    onChange={(e) =>
                      setFrequencyUnit(e.target.value as FrequencyUnitEnum)
                    }
                    IconComponent={KeyboardArrowDown}
                  >
                    {Object.values(FrequencyUnitEnum).map((unit) => (
                      <MenuItem key={unit} value={unit}>
                        {unit}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </Stack>
          )}

          {/* Name and Subject row */}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 0.75, fontSize: "0.875rem" }}
              >
                Name
                <Box component="span" sx={{ color: `var(--error-700)` }}>
                  *
                </Box>
              </Typography>
              <TextField
                fullWidth
                placeholder="e.g. Welcome email"
                value={name}
                name="name"
                onChange={(e) => {
                  setName(e.target.value);
                  handleChange(e, "Name");
                }}
                size="small"
                required
                error={Boolean(errors.name)}
                helperText={errors.name || " "}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 0.75, fontSize: "0.875rem" }}
              >
                Subject
                <Box component="span" sx={{ color: `var(--error-700)` }}>
                  *
                </Box>
              </Typography>
              <TextField
                fullWidth
                placeholder="Subject"
                name="subject"
                value={subject}
                onChange={(e) => {
                  setSubject(e.target.value);
                  handleChange(e, "Subject");
                }}
                size="small"
                required
                error={!!errors.subject}
                helperText={errors.subject || " "}
              />
            </Box>
          </Stack>

          {/* Default sender */}
          <Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 0.75, fontSize: "0.875rem" }}
            >
              Default sender
            </Typography>
            <FormControl fullWidth>
              <Select
                value={sender}
                onChange={(e) => setSender(e.target.value)}
                displayEmpty
                size="small"
              >
                <MenuItem value="elearning@igacademy.com">
                  elearning@igacademy.com
                </MenuItem>
                <MenuItem value="success@igacademy.com">
                  success@igacademy.com
                </MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Message editor */}
          <Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 0.75, fontSize: "0.875rem" }}
            >
              Message
              <Box component="span" sx={{ color: `var(--error-700)` }}>
                *
              </Box>
            </Typography>
            <QuillEditor
              value={message}
              onChange={setMessage}
              type={emailTemplateType}
            />
          </Box>

          {/* Save button */}
          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <Button
              onClick={handleSave}
              disabled={loading}
              sx={{
                px: 2,
                borderRadius: "12px",
                backgroundColor: `${brand[600]} !important`,
                color: `white`,
                "&:hover": {
                  bgcolor: `${brand[700]} !important`,
                },
              }}
            >
              Save template
            </Button>
          </Box>
        </Stack>
      </Box>
    </Paper>
  );
};

export default EmailTemplateEditor;
